import type { CommentStatus, EngagementStatus } from '../../graphqlEnums'

/**
 * How a resolved card came to rest. A dismiss leaves the reopen ("Undo
 * dismiss") affordance; a reply surfaces the published replies instead.
 */
export type ResolvedVia = 'dismiss' | 'reply'

/**
 * The read-side resolution phase of an engagement card (comment or mention),
 * derived purely from server status + list-owned / card-owned resolution flags.
 *
 * - `active`: not resolved — still awaiting action (covers comment-only
 *   `'new'` / `'replying'` and every `'unreplied'` state).
 * - `resolved`: dismissed or replied. `expanded` records whether the list is
 *   currently holding the resolved card open (the "interim expanded" state),
 *   which returns it to full colour/chrome without un-resolving it.
 *
 * This is the read-side selector only — it does not model the dismiss/reply
 * transitions, just how to render the current inputs.
 */
export type CardPhase =
  | { kind: 'active' }
  | { kind: 'resolved'; via: ResolvedVia; expanded: boolean }

export type SelectCardPhaseInput = {
  /** Server status. Comment's enum additionally carries `'new'`/`'replying'`. */
  status: EngagementStatus | CommentStatus
  /** List-owned: is the card currently held open by the list. */
  expanded: boolean
  /** List-owned: is the card in the list's local resolution set. */
  isLocallyResolved: boolean
  /** Card-owned: did a just-published reply (not a dismiss) resolve it. */
  isLocallyReplied: boolean
}

/**
 * Collapses the six-boolean resolution derivation that `useCommentState` and
 * `useMentionState` each duplicated into one pure selector.
 *
 * `via` resolves server status first, then local intent: a server `'dismissed'`
 * / `'replied'` status wins over the local flags, and only when neither applies
 * does an unstatused-but-locally-resolved card fall back to the card-owned
 * `isLocallyReplied` flag to tell a local reply apart from a local dismiss.
 */
export const selectCardPhase = ({
  status,
  expanded,
  isLocallyResolved,
  isLocallyReplied,
}: SelectCardPhaseInput): CardPhase => {
  const via: ResolvedVia | null =
    status === 'dismissed'
      ? 'dismiss'
      : status === 'replied'
        ? 'reply'
        : isLocallyResolved
          ? isLocallyReplied
            ? 'reply'
            : 'dismiss'
          : null

  if (via === null) {
    return { kind: 'active' }
  }

  return { kind: 'resolved', via, expanded }
}

/** Resolved and at rest (greyed out, collapsed) — not held open by the list. */
export const isResolved = (phase: CardPhase): boolean =>
  phase.kind === 'resolved' && !phase.expanded

/** Resolved but currently held open by the list (full colour/chrome). */
export const isInterimExpanded = (phase: CardPhase): boolean =>
  phase.kind === 'resolved' && phase.expanded

/** Resolved via dismiss — gets the reopen ("Undo dismiss") affordance. */
export const isDismissed = (phase: CardPhase): boolean =>
  phase.kind === 'resolved' && phase.via === 'dismiss'

/** Resolved at all, regardless of whether it is currently expanded. */
export const isResolvedByStatus = (phase: CardPhase): boolean =>
  phase.kind === 'resolved'
